/* eslint-disable @typescript-eslint/no-use-before-define */
import { Typography } from '@euler/app/components/typography';
import { useIsAsideLayout } from '@euler/components/Aside';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, memo, ReactNode } from 'react';
import {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSubmitEditingEventData,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { css } from 'styled-components';

type SearchInputProps = {
  autoCorrect?: boolean;
  autoCapitalize?: TextInputProps['autoCapitalize'];
  searchInputEditable?: boolean;
  searchText?: string;
  searchPlaceholder?: string;
  searchPlaceholderTextColor?: string;
  onSearchInputPress?: () => void;
  onSearchInputChange?: (text: string) => void;
  onSearch?: (text: string) => void;
};

type Props = {
  onLayout?: (e: LayoutChangeEvent) => void;
} & SearchInputProps;

const kSearchInputHeight = 36;
const kNavButtonSize = 36;

export const SearchNavHeader = memo(
  ({ onLayout, ...searchInputProps }: Props) => {
    return (
      <View onLayout={onLayout}>
        {/** shade */}
        <LinearGradient
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 1 }}
          colors={['#006be4', '#5ba0e0']}
          locations={[0.335, 1]}
          style={StyleSheet.absoluteFill}
          css={`
            flex: 1;
          `}
        />

        {/** search bar */}
        <SearchNavBar {...searchInputProps} />
      </View>
    );
  },
);

const NavButton: FC<{
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}> = memo(({ style, onPress, children }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      css={`
        padding-left: ${rpx(8)}px;
        padding-right: ${rpx(8)}px;
        height: ${rpx(kNavButtonSize)}px;
        align-items: center;
        justify-content: center;
      `}
      style={style}
    >
      {children}
    </TouchableOpacity>
  );
});

const SearchBox = memo(
  ({
    autoCapitalize,
    autoCorrect,
    onSearchInputPress,
    searchText,
    searchPlaceholder,
    searchPlaceholderTextColor,
    searchInputEditable,
    onSearchInputChange,
    onSearch,
  }: SearchInputProps) => {
    const { rpx } = useResponsiveContext();

    const onSubmitEditing = usePersistFn(
      (_e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        onSearch?.(searchText ?? '');
      },
    );

    const onKeyPress = usePersistFn(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== 'web') return;
        if (e.nativeEvent.key === 'Enter') {
          onSearch?.(searchText ?? '');
        }
      },
    );

    const wrapperCss = css`
      background-color: #fff;
      height: ${rpx(kSearchInputHeight)}px;
      border-radius: ${rpx(kSearchInputHeight) / 2}px;
      padding: 0 ${rpx(15)}px;
      flex: 1;
    `;

    if (searchInputEditable) {
      return (
        <View
          css={`
            ${wrapperCss}
          `}
        >
          <TextInput
            placeholder={searchPlaceholder}
            placeholderTextColor={searchPlaceholderTextColor}
            value={searchText ?? ''}
            returnKeyType="search"
            clearButtonMode="always"
            autoFocus
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            onChangeText={onSearchInputChange}
            onSubmitEditing={onSubmitEditing}
            onKeyPress={onKeyPress}
            css={`
              flex: 1;
            `}
          />
        </View>
      );
    }
    return (
      <TouchableOpacity
        onPress={onSearchInputPress}
        activeOpacity={0.9}
        css={`
          ${wrapperCss};
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        `}
      >
        <Icon name="search" size={rpx(16)} color="#999" />
        <Typography.Label
          size={rpx(14)}
          light
          color="#999"
          css={`
            margin-left: ${rpx(10)}px;
          `}
        >
          {searchPlaceholder ?? ''}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const SearchNavBar = memo(
  ({
    style,
    ...searchInputProps
  }: {
    style?: StyleProp<ViewStyle>;
  } & SearchInputProps) => {
    const navigation = useNavigation();
    const { navBarHeight, statusBarHeight, safeAreaInsets } =
      useSystemMetrics();
    const { rpx } = useResponsiveContext();
    const isAside = useIsAsideLayout();
    const top = safeAreaInsets.top + (isAside ? statusBarHeight / 2 : 0);
    const height = navBarHeight + rpx(10);

    return (
      <View
        style={style}
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: nowrap;
          height: ${height}px;
          padding-top: ${top}px;
        `}
      >
        <NavButton onPress={() => navigation.goBack()}>
          <Icon name="chevron-left" size={rpx(34)} color="#fff" />
        </NavButton>
        <SearchBox {...searchInputProps} />
        <View
          css={`
            width: ${rpx(kNavButtonSize + 10)}px;
          `}
        />
      </View>
    );
  },
);
